/**
 * Étape 1 — le cahier de charges (chunk 9.AZ.1).
 *
 * C'est ce que l'assureur recevra. Tant qu'il n'y en a pas, il n'y a pas de
 * demande de soumission : « pouvoir sélectionner Sunlife pour moi n'est pas une
 * soumission, je dois lui dire ce que je veux ».
 *
 * Trois états, trois rendus distincts — jamais un seul avec du texte de
 * remplacement : rien à rattacher, un document prêt, un document à refaire.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useFormResponseDrafts,
  type FormResponseListItem,
} from "../../../lib/queries/formResponses";
import { useFormTemplates } from "../../../lib/queries/formTemplates";
import type { QuoteRequestSpecRow } from "../../../lib/queries/quoteRequests";
import { construireDetour } from "../../../lib/retourParcours";

import { Etape } from "./Etape";
import { FormulairesRequis } from "./FormulairesRequis";

export function EtapeCahierDeCharges(props: {
  /**
   * Le rang de l'étape dans le parcours.
   *
   * Paramétré depuis 9.BK.3 : l'étape de l'effectif s'intercale quand le
   * dossier en porte un, et **disparaît** sinon. Un numéro codé en dur
   * produirait une suite trouée — 1, 3, 4 — qui ferait chercher l'étape
   * manquante. La page connaît les étapes rendues ; elle les numérote.
   */
  numero?: number;
  cahier: QuoteRequestSpecRow | null;
  aRefaire: boolean;
  chargement: boolean;
  analyses: readonly FormResponseListItem[];
  modifiable: boolean;
  enCours: boolean;
  /** La demande en préparation — elle porte le retour du détour de remplissage. */
  demandeId: string;
  /**
   * Le dossier — c'est lui qui porte les exigences, pas la demande.
   *
   * Absent tant que la demande n'est pas chargée : le bloc des formulaires
   * requis ne rend alors rien, plutôt que d'afficher un vide trompeur.
   */
  projectId?: string;
  onChoisir: (formResponseId: string) => void;
  onRefaire: () => void;
  onTelecharger: (fileId: string) => void;
}) {
  const { t, i18n } = useTranslation();

  const etat = props.aRefaire
    ? ("stale" as const)
    : props.cahier
      ? ("ready" as const)
      : ("empty" as const);

  return (
    <Etape
      numero={props.numero ?? 1}
      titre={t("prepare_send.spec.title")}
      description={t(
        props.modifiable ? "prepare_send.spec.subtitle" : "preparation_context.history_help",
      )}
      etat={etat}
      statusLabel={
        !props.modifiable
          ? t(props.cahier ? "flow.document_available" : "flow.no_document")
          : undefined
      }
    >
      {props.chargement && (
        <div aria-busy="true" aria-live="polite">
          <span className="sr-only">{t("app.loading")}</span>
          <div className="h-16 animate-pulse rounded-lg bg-slate-100 motion-reduce:animate-none" />
        </div>
      )}

      {props.modifiable && props.projectId && (
        <DraftCahiers
          key={props.projectId}
          projectId={props.projectId}
          demandeId={props.demandeId}
        />
      )}

      {!props.chargement && etat === "empty" && props.modifiable && (
        <ChoixAnalyse
          analyses={props.analyses}
          modifiable={props.modifiable}
          enCours={props.enCours}
          demandeId={props.demandeId}
          onChoisir={props.onChoisir}
        />
      )}

      {!props.chargement && etat === "empty" && !props.modifiable && (
        <p className="text-sm text-slate-600">{t("preparation_context.history_no_spec")}</p>
      )}

      {!props.chargement && props.cahier && (
        <div className="space-y-3">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">
                {t("prepare_send.spec.document")}
              </dt>
              <dd className="text-sm text-slate-900">
                {props.cahier.artifact_file_id ? (
                  <button
                    type="button"
                    onClick={() => props.onTelecharger(props.cahier!.artifact_file_id!)}
                    className="focus-visible:ring-brand-500 text-brand-700 hover:text-brand-800 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                  >
                    {t("prepare_send.spec.open_document")}
                  </button>
                ) : (
                  t("prepare_send.spec.no_document")
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">
                {t("prepare_send.spec.prepared_on")}
              </dt>
              <dd className="text-sm tabular-nums text-slate-900">
                {props.cahier.rendered_at
                  ? new Date(props.cahier.rendered_at).toLocaleDateString(i18n.language)
                  : "—"}
              </dd>
            </div>
          </dl>

          {props.aRefaire && props.modifiable && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900"
            >
              <p className="font-medium">{t("prepare_send.spec.stale_title")}</p>
              <p className="mt-1">{t("prepare_send.spec.stale_body")}</p>
              <button
                type="button"
                onClick={props.onRefaire}
                disabled={props.enCours}
                aria-busy={props.enCours}
                className="focus-visible:ring-brand-500 mt-3 touch-manipulation rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {props.enCours
                  ? t("prepare_send.spec.regenerating")
                  : t("prepare_send.spec.regenerate")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Les requis courants du dossier appartiennent à la préparation, pas
          à l’historique d’une demande : leurs liens ouvrent une nouvelle saisie. */}
      {props.modifiable && (
        <FormulairesRequis projectId={props.projectId} demandeId={props.demandeId} />
      )}
    </Etape>
  );
}

/**
 * L'état vide est une INVITATION : il propose les analyses du dossier, et à
 * défaut les modèles publiés à remplir sur place.
 *
 * 9.BB.1 — il envoyait auparavant vers `/formulaires`, l'administration des
 * modèles. Le conseiller devait y ouvrir un modèle, trouver « Remplir », puis
 * rechoisir à la main le dossier d'où il venait. Six clics et un contexte perdu
 * pour un geste qui en vaut deux : choisir un modèle, le remplir.
 */
function ChoixAnalyse(props: {
  analyses: readonly FormResponseListItem[];
  modifiable: boolean;
  enCours: boolean;
  demandeId: string;
  onChoisir: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();

  if (
    props.analyses.filter((a) => a.purpose === "business_spec" && a.catalog_usage === "business")
      .length === 0
  ) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6">
        <p className="text-center text-sm font-medium text-slate-800">
          {t("prepare_send.spec.no_analysis_title")}
        </p>
        <p className="mt-1 text-center text-sm text-slate-600">
          {t("prepare_send.spec.no_analysis_body")}
        </p>
        <ModelesARemplir demandeId={props.demandeId} />
      </div>
    );
  }

  return (
    <>
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {props.analyses
          .filter((a) => a.purpose === "business_spec" && a.catalog_usage === "business")
          .map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">
                  {a.template_name ?? t("prepare_send.spec.unnamed_analysis")}
                </p>
                <p className="text-xs tabular-nums text-slate-500">
                  {a.submitted_at
                    ? new Date(a.submitted_at).toLocaleDateString(i18n.language)
                    : "—"}
                </p>
              </div>
              <button
                type="button"
                disabled={!props.modifiable || props.enCours}
                onClick={() => props.onChoisir(a.id)}
                className="focus-visible:ring-brand-500 shrink-0 touch-manipulation rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40"
              >
                {t("prepare_send.spec.use_this")}
              </button>
            </li>
          ))}
      </ul>
      {/* Une analyse existe déjà, mais ce n'est peut-être pas la bonne : on peut
        toujours en remplir une neuve sans quitter la page. */}
      <ModelesARemplir demandeId={props.demandeId} />
    </>
  );
}

/**
 * Les modèles publiés du cabinet, remplissables sur place.
 *
 * Le lien porte la destination de retour ET l'identifiant de la demande : c'est
 * lui qui permettra au remplissage de savoir à quel dossier rattacher, et où
 * revenir. Un modèle sans version publiée n'est pas proposé — il n'y aurait
 * rien à remplir.
 */
function ModelesARemplir({ demandeId }: { demandeId: string }) {
  const { t } = useTranslation();
  const modeles = useFormTemplates({ status: "active", catalog_usage: "business" });

  const remplissables = (modeles.data?.items ?? []).filter(
    (m) =>
      m.latest_published_version_id !== null &&
      m.latest_published_purpose === "business_spec" &&
      m.catalog_usage === "business" &&
      !m.supports_client_signature,
  );

  if (modeles.isPending) {
    return (
      <div className="mt-4" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t("app.loading")}</span>
        <div className="h-10 animate-pulse rounded-lg bg-slate-100 motion-reduce:animate-none" />
      </div>
    );
  }

  if (modeles.isError) return <p role="alert">{t("errors.generic")}</p>;

  if (remplissables.length === 0) {
    return (
      <p
        role="status"
        className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
      >
        {t("request_flow.no_business_template")}{" "}
        <Link
          to="/formulaires"
          className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 rounded font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2"
        >
          {t("prepare_send.spec.no_template_cta")}
        </Link>
      </p>
    );
  }

  return (
    <div className="mt-4">
      <p className="text-sm font-medium text-slate-800">{t("prepare_send.spec.fill_new_title")}</p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {remplissables.map((m) => (
          <li key={m.id}>
            <Link
              to={construireDetour({
                vers: `/formulaires/${m.id}/versions/${m.latest_published_version_id}/remplir`,
                destination: "demande-preparer",
                retourId: demandeId,
              })}
              className="btn-primary min-h-11"
            >
              {t("request_flow.fill_spec")} — {m.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DraftCahiers({ projectId, demandeId }: { projectId: string; demandeId: string }) {
  const { t, i18n } = useTranslation();
  const drafts = useFormResponseDrafts(projectId);
  if (drafts.isPending) return <p aria-live="polite">{t("app.loading")}</p>;
  if (drafts.isError) return <p role="alert">{t("form_templates.fill.drafts_unavailable")}</p>;
  const items = (drafts.data ?? []).filter(
    (r) => r.status === "draft" && r.purpose === "business_spec" && r.project_id === projectId,
  );
  if (!items.length) return null;
  return (
    <ul className="mb-4 space-y-3">
      {items.map((r) => (
        <li key={r.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="break-words text-sm font-medium">
            {r.template_name} ·{" "}
            {t("form_responses.filters.version_option", { number: r.version_number })}
          </p>
          <p className="text-xs text-slate-600">
            {new Date(r.created_at).toLocaleString(i18n.language)}
          </p>
          <Link
            className="text-brand-700 mt-2 inline-flex min-h-11 items-center font-medium underline"
            to={`${construireDetour({ vers: `/formulaires/${r.template_id}/versions/${r.template_version_id}/remplir`, destination: "demande-preparer", retourId: demandeId })}&response_id=${r.id}`}
          >
            {t("form_templates.fill.resume_draft")}
          </Link>
        </li>
      ))}
    </ul>
  );
}
